import { QBankStructureStatus, QuestionStatus } from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import {
  PaperSection,
  Flex,
  Text,
  ChipSelection,
  NoRowsOverlay,
  ListAltOutlinedIcon,
  type OptionSelectionProps,
} from '@shared/components';
import { useState } from 'react';

import { useQBankStructureDashBoardSearchParams } from '../../hooks';
import { QBankStructureItemSetQuestionDashBoard } from '../qbank-structure-item-set-question-dashboard';
import { QBankStructureLosItemSetQuestionDashBoard } from '../qbank-structure-los-item-set-question-dashboard';
import { QBankStructureLOSSingleQuestionDashboard } from '../qbank-structure-los-single-question-dashboard';
import { QBankStructureSingleQuestionDashBoard } from '../qbank-structure-single-question-dashboard';

type DashboardAction = 'single-question' | 'item-set' | 'los-single' | 'los-item-set';

interface DashboardOption extends OptionSelectionProps {
  label: string;
  value: DashboardAction;
}

const DASHBOARD_ACTION_OPTIONS: DashboardOption[] = [
  { label: 'Single & LOQ', value: 'single-question' },
  { label: 'Item Set', value: 'item-set' },
  { label: 'LOS: Single', value: 'los-single' },
  { label: 'LOS: Item Set', value: 'los-item-set' },
];

export default function QbankStructureDashboard() {
  const [action, setAction] = useState<DashboardAction>('single-question');
  const [, { changeStatus }] = useQBankStructureDashBoardSearchParams();
  const { qBankStructureInformation } = useQBankStructure();

  const handleActionChange = (newAction: DashboardAction) => {
    changeStatus(QuestionStatus.Published);
    setAction(newAction);
  };
  const qbankStructureStatus = qBankStructureInformation?.status;
  const allowedDashboardStatus = [
    QBankStructureStatus.Published,
    QBankStructureStatus.Archived,
    QBankStructureStatus.Unpublished,
  ];

  const actionRenderer = (action: DashboardAction) => {
    switch (action) {
      case 'single-question':
        return <QBankStructureSingleQuestionDashBoard />;
      case 'item-set':
        return <QBankStructureItemSetQuestionDashBoard />;
      case 'los-single':
        return <QBankStructureLOSSingleQuestionDashboard />;
      case 'los-item-set':
        return <QBankStructureLosItemSetQuestionDashBoard />;
    }
  };

  return (
    <PaperSection title='Dashboard'>
      <Flex
        sx={(theme) => ({ p: theme.spacing(6), gap: theme.spacing(6), flexDirection: 'column' })}
      >
        {allowedDashboardStatus.includes(qbankStructureStatus) ? (
          <>
            <Flex sx={{ gap: 2, alignItems: 'center' }}>
              <Text variant='body2Semibold'>Table View:</Text>
              <ChipSelection
                options={DASHBOARD_ACTION_OPTIONS}
                row
                value={action}
                onChange={(value) => {
                  if (typeof value === 'string') handleActionChange(value as DashboardAction);
                }}
                sx={(theme) => ({
                  color: theme.palette.grey[500],
                  alignItems: 'flex-start',
                  '& .MuiChip-root': {
                    border: 'none',
                    backgroundColor: theme.palette.grey[100],
                    width: 'auto',
                    height: '32px',
                    p: theme.spacing(1.5, 4),
                    '&.MuiChip-clickable:hover': {
                      backgroundColor: theme.palette.grey[100],
                      '&.MuiChip-clickableColorPrimary': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.text.primary,
                      },
                    },
                    '&.MuiChip-clickableColorPrimary': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.text.primary,
                    },
                  },
                })}
              />
            </Flex>

            {actionRenderer(action)}
          </>
        ) : (
          <NoRowsOverlay
            title='ยังไม่มีข้อมูลสำหรับแสดง Dashboard'
            description='ระบบจะแสดงภาพรวมคลังข้อสอบเมื่อคุณกำหนดโครงสร้างคลังข้อสอบเรียบร้อยแล้ว'
            icon={ListAltOutlinedIcon}
            slotProps={{
              wrapper: { disablePadding: true, sx: (theme) => ({ p: theme.spacing(20) }) },
            }}
          />
        )}
      </Flex>
    </PaperSection>
  );
}
